import React, { useEffect, useMemo, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import { submittalsAPI } from '../../services/authService';
import { notifyError, notifySuccess } from '../../utils/notify';
import { getTodayISO } from '../../utils/dateFormat';

const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];

const CreateSubmittal = ({ onCreated, onCancel }) => {
  const { projects } = useProjects();
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    project_id: '',
    category_id: '',
    template_id: '',
    subject: '',
    description: '',
    discipline: '',
    boq_reference: '',
    contract_reference: '',
    location_block: '',
    priority: 'NORMAL',
    due_date: '',
    remarks: '',
    dynamic_field_values: {},
  });

  useEffect(() => {
    submittalsAPI.getCategories()
      .then((resp) => setCategories(safeArray(resp?.data?.data)))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!form.category_id) {
      setTemplates([]);
      setFields([]);
      return;
    }
    submittalsAPI.getTemplates({ categoryId: form.category_id })
      .then((resp) => {
        const list = safeArray(resp?.data?.data);
        setTemplates(list);
        const defaultTpl = list.find((t) => t.isDefault) || list[0];
        if (defaultTpl && !form.template_id) {
          setForm((prev) => ({ ...prev, template_id: String(defaultTpl.id) }));
        }
      })
      .catch(() => setTemplates([]));
  }, [form.category_id]);

  useEffect(() => {
    if (!form.template_id) {
      setFields([]);
      return;
    }
    submittalsAPI.getTemplateFields(form.template_id)
      .then((resp) => setFields(safeArray(resp?.data?.data)))
      .catch(() => setFields([]));
  }, [form.template_id]);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(form.category_id)),
    [categories, form.category_id]
  );

  useEffect(() => {
    if (selectedCategory?.default_review_days && !form.due_date) {
      const d = new Date();
      d.setDate(d.getDate() + Number(selectedCategory.default_review_days));
      setForm((prev) => ({ ...prev, due_date: d.toISOString().slice(0, 10) }));
    }
  }, [selectedCategory]);

  const setDynamic = (key, value) => {
    setForm((prev) => ({
      ...prev,
      dynamic_field_values: { ...prev.dynamic_field_values, [key]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.category_id || !form.subject.trim()) {
      notifyError('Project, category, and subject are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_id: parseInt(form.project_id, 10),
        category_id: parseInt(form.category_id, 10),
        template_id: form.template_id ? parseInt(form.template_id, 10) : undefined,
        subject: form.subject.trim(),
        description: form.description || null,
        discipline: form.discipline || null,
        boq_reference: form.boq_reference || null,
        contract_reference: form.contract_reference || null,
        location_block: form.location_block || null,
        priority: form.priority,
        due_date: form.due_date || null,
        remarks: form.remarks || null,
        dynamic_field_values: form.dynamic_field_values,
      };
      const resp = await submittalsAPI.create(payload);
      notifySuccess('Submittal draft created.');
      if (typeof onCreated === 'function') onCreated(resp?.data?.id ?? resp?.data?.data?.id);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to create submittal.');
    } finally {
      setSaving(false);
    }
  };

  const renderDynamicField = (field) => {
    const key = field.field_key;
    const val = form.dynamic_field_values[key];
    const common = {
      className: 'form-input',
      value: val ?? '',
      onChange: (e) => setDynamic(key, e.target.value),
      required: field.is_required,
    };

    if (field.field_type === 'BOOLEAN') {
      return (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={!!val}
            onChange={(e) => setDynamic(key, e.target.checked)}
          />
          <span>{field.label}</span>
        </label>
      );
    }

    if (field.field_type === 'MULTI_SELECT' && Array.isArray(field.options)) {
      const selected = Array.isArray(val)
        ? val.map(String)
        : (val ? String(val).split(',').map((s) => s.trim()).filter(Boolean) : []);
      return (
        <select
          multiple
          className="form-input"
          value={selected}
          onChange={(e) => {
            const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
            setDynamic(key, opts);
          }}
        >
          {field.options.map((opt) => (
            <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
          ))}
        </select>
      );
    }

    if (field.field_type === 'FILE') {
      return (
        <div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
            Upload files after creating the submittal draft via the Attachments section.
          </p>
          <input
            type="text"
            className="form-input"
            placeholder="File reference / marker text"
            value={val ?? ''}
            onChange={(e) => setDynamic(key, e.target.value)}
          />
        </div>
      );
    }

    if (field.field_type === 'USER') {
      return (
        <input
          type="number"
          className="form-input"
          placeholder="User ID"
          value={val ?? ''}
          onChange={(e) => setDynamic(key, e.target.value ? Number(e.target.value) : '')}
          required={field.is_required}
        />
      );
    }

    if (field.field_type === 'PARTY') {
      return (
        <input
          type="number"
          className="form-input"
          placeholder="Party ID"
          value={val ?? ''}
          onChange={(e) => setDynamic(key, e.target.value ? Number(e.target.value) : '')}
          required={field.is_required}
        />
      );
    }

    if (field.field_type === 'CHECKLIST') {
      const items = Array.isArray(val) && val.length
        ? val
        : [{ label: '', checked: false, remarks: '' }];
      const updateItems = (next) => setDynamic(key, next);
      return (
        <div>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={!!item.checked}
                onChange={(e) => {
                  const next = items.map((row, i) => (i === idx ? { ...row, checked: e.target.checked } : row));
                  updateItems(next);
                }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="Label"
                value={item.label || ''}
                onChange={(e) => {
                  const next = items.map((row, i) => (i === idx ? { ...row, label: e.target.value } : row));
                  updateItems(next);
                }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="Remarks"
                value={item.remarks || ''}
                onChange={(e) => {
                  const next = items.map((row, i) => (i === idx ? { ...row, remarks: e.target.value } : row));
                  updateItems(next);
                }}
              />
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => updateItems([...items, { label: '', checked: false, remarks: '' }])}
          >
            Add checklist item
          </button>
        </div>
      );
    }

    if (field.field_type === 'TEXTAREA') {
      return <textarea {...common} rows={3} />;
    }
    if (field.field_type === 'SELECT' && Array.isArray(field.options)) {
      return (
        <select {...common} onChange={(e) => setDynamic(key, e.target.value)}>
          <option value="">Select...</option>
          {field.options.map((opt) => (
            <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
          ))}
        </select>
      );
    }
    if (field.field_type === 'DATE') {
      return <input type="date" {...common} />;
    }
    if (field.field_type === 'NUMBER') {
      return <input type="number" {...common} />;
    }
    return <input type="text" {...common} />;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Create Submittal</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>Create a new submittal draft with category template fields.</p>
        </div>
        {typeof onCancel === 'function' && (
          <button type="button" className="btn-secondary btn-sm" onClick={onCancel}>Back to Register</button>
        )}
      </div>

      <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label>Project *</label>
            <select className="form-input" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required>
              <option value="">Select project</option>
              {safeArray(projects).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Category *</label>
            <select className="form-input" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, template_id: '', dynamic_field_values: {} })} required>
              <option value="">Select category</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Template</label>
            <select className="form-input" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value, dynamic_field_values: {} })}>
              <option value="">Default template</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (Default)' : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select className="form-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Subject *</label>
            <input className="form-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea className="form-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Discipline</label>
            <input className="form-input" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Location / Block</label>
            <input className="form-input" value={form.location_block} onChange={(e) => setForm({ ...form, location_block: e.target.value })} />
          </div>
          <div className="form-group">
            <label>BOQ Reference</label>
            <input className="form-input" value={form.boq_reference} onChange={(e) => setForm({ ...form, boq_reference: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Contract Reference</label>
            <input className="form-input" value={form.contract_reference} onChange={(e) => setForm({ ...form, contract_reference: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Due Date</label>
            <input type="date" className="form-input" value={form.due_date} min={getTodayISO()} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Initial Remarks</label>
            <input className="form-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </div>
        </div>

        {form.category_id && form.template_id && fields.length === 0 && (
          <div style={{ marginTop: 24, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontSize: 13 }}>
            No sample fields found for this category. Admin can configure fields from Templates Admin.
          </div>
        )}

        {fields.length > 0 && (
          <div style={{ marginTop: 24, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <h3 style={{ fontSize: 16, marginBottom: 12 }}>Template Fields</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {fields.map((field) => (
                <div key={field.id} className="form-group">
                  <label>{field.label}{field.is_required ? ' *' : ''}</label>
                  {renderDynamicField(field)}
                  {field.help_text && <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>{field.help_text}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Draft'}</button>
          {typeof onCancel === 'function' && (
            <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateSubmittal;
